// Captures metadata about the DOM element the user Ctrl+Alt+Right-clicked.
// This context is shown in the Guybrush dialog and sent to the LLM so it
// knows which element's styles to modify.
export interface ElementContext {
    tagName: string;
    id: string | undefined;
    classList: string[];
    parentChain: string;
    outerHtmlSnippet: string;
    computedStyles: Record<string, string>;
    nearbyHtmlTree: string;
}

// Subset of computed style properties sent to the LLM for context.
// Kept small to avoid bloating the prompt while still giving the model
// enough info to write targeted CSS rules.
const RELEVANT_STYLE_PROPERTIES = [
    "display",
    "position",
    "width",
    "height",
    "margin",
    "padding",
    "fontSize",
    "fontWeight",
    "color",
    "backgroundColor",
    "border",
    "borderRadius",
    "flexDirection",
    "justifyContent",
    "alignItems",
    "gap",
    "overflow",
];

const MAX_OUTER_HTML_LENGTH = 500;
const MAX_PARENT_CHAIN_DEPTH = 5;
const MAX_NEARBY_TREE_DEPTH = 3;
const MAX_SIBLING_COUNT = 5;

function buildSelector(el: HTMLElement): string {
    let selector = el.tagName.toLowerCase();
    if (el.id) {
        selector += `#${el.id}`;
    }
    const classes = Array.from(el.classList).slice(0, 3);
    if (classes.length > 0) {
        selector += `.${classes.join(".")}`;
    }
    return selector;
}

function buildParentChain(el: HTMLElement): string {
    const chain: string[] = [];
    let current: HTMLElement | null = el;
    let depth = 0;

    while (current && depth < MAX_PARENT_CHAIN_DEPTH) {
        chain.unshift(buildSelector(current));
        current = current.parentElement;
        depth++;
    }

    return chain.join(" > ");
}

function getRelevantComputedStyles(el: HTMLElement): Record<string, string> {
    const computed = window.getComputedStyle(el);
    const styles: Record<string, string> = {};

    for (const prop of RELEVANT_STYLE_PROPERTIES) {
        const value = computed.getPropertyValue(prop.replace(/[A-Z]/g, (m) => `-${m.toLowerCase()}`));
        if (value && value !== "" && value !== "none" && value !== "normal" && value !== "auto") {
            styles[prop] = value;
        }
    }

    return styles;
}

// Builds an indented HTML tree excerpt around the target element, showing
// parent containers and siblings so the LLM understands the layout structure.
function buildNearbyHtmlTree(el: HTMLElement): string {
    const lines: string[] = [];

    const ancestors: HTMLElement[] = [];
    let current: HTMLElement | null = el;
    for (let i = 0; i < MAX_NEARBY_TREE_DEPTH && current; i++) {
        ancestors.unshift(current);
        current = current.parentElement;
    }

    for (let i = 0; i < ancestors.length; i++) {
        const ancestor = ancestors[i];
        if (!ancestor) continue;
        const indent = "  ".repeat(i);
        const isLast = i === ancestors.length - 1;

        if (isLast) {
            const parent = ancestor.parentElement;
            if (parent) {
                const siblings = Array.from(parent.children).filter(
                    (child): child is HTMLElement => child instanceof HTMLElement,
                );
                const targetIndex = siblings.indexOf(ancestor);
                const start = Math.max(
                    0,
                    Math.min(targetIndex - Math.floor(MAX_SIBLING_COUNT / 2), siblings.length - MAX_SIBLING_COUNT),
                );
                const end = Math.min(siblings.length, start + MAX_SIBLING_COUNT);

                if (start > 0) {
                    lines.push(`${indent}... (${start} more siblings above)`);
                }
                for (let j = start; j < end; j++) {
                    const sibling = siblings[j];
                    if (!sibling) continue;
                    const marker = j === targetIndex ? " ← target" : "";
                    const childCount = sibling.children.length;
                    const childInfo =
                        childCount > 0 ? ` (${childCount} ${childCount === 1 ? "child" : "children"})` : "";
                    lines.push(`${indent}<${buildSelector(sibling)}>${childInfo}${marker}`);
                }
                if (end < siblings.length) {
                    lines.push(`${indent}... (${siblings.length - end} more siblings below)`);
                }
            } else {
                lines.push(`${indent}<${buildSelector(ancestor)}> ← target`);
            }
        } else {
            lines.push(`${indent}<${buildSelector(ancestor)}>`);
        }
    }

    return lines.join("\n");
}

export function buildElementContext(el: HTMLElement): ElementContext {
    let outerHtmlSnippet = el.outerHTML;
    if (outerHtmlSnippet.length > MAX_OUTER_HTML_LENGTH) {
        outerHtmlSnippet = outerHtmlSnippet.slice(0, MAX_OUTER_HTML_LENGTH) + "...";
    }

    return {
        tagName: el.tagName.toLowerCase(),
        id: el.id || undefined,
        classList: Array.from(el.classList),
        parentChain: buildParentChain(el),
        outerHtmlSnippet,
        computedStyles: getRelevantComputedStyles(el),
        nearbyHtmlTree: buildNearbyHtmlTree(el),
    };
}

// Formats element context into a human-readable string included in the
// Devin handoff prompt ("Make it so Devin" button).
export function formatElementContextForPrompt(context: ElementContext): string {
    const parts: string[] = [];

    parts.push(`Target element: <${context.tagName}>`);

    if (context.id) {
        parts.push(`ID: ${context.id}`);
    }

    if (context.classList.length > 0) {
        parts.push(`Classes: ${context.classList.join(", ")}`);
    }

    parts.push(`Selector path: ${context.parentChain}`);

    const styleEntries = Object.entries(context.computedStyles);
    if (styleEntries.length > 0) {
        parts.push(`Current styles: ${styleEntries.map(([k, v]) => `${k}: ${v}`).join("; ")}`);
    }

    parts.push(`HTML snippet: ${context.outerHtmlSnippet}`);

    if (context.nearbyHtmlTree) {
        parts.push(`Nearby HTML tree:\n${context.nearbyHtmlTree}`);
    }

    return parts.join("\n");
}
